import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  useRouterState,
  Navigate,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { CartProvider } from "@/lib/cart-context";
import { CategorySheetProvider } from "@/lib/category-sheet-context";
import { ServiceSheetProvider } from "@/lib/service-sheet-context";
import { useNotificationRouter } from "@/lib/notifications";
import { useFcmRegistration } from "@/lib/fcm";
import { useAddresses, useBookings, useProviderProfile } from "@/hooks/useApi";
import { isTeamPro, useProMe } from "@/hooks/usePro";
import { useProHeartbeatHost } from "@/hooks/useProHeartbeat";
import { syncPendingInvite } from "@/lib/invite";
import { adminGate } from "@/lib/admin";
import { AdminMenu } from "@/components/admin/AdminMenu";
import {
  consumesLanding,
  nextLandedFor,
  proGateVerdict,
  providerSeesInviteRoute,
  skipsLocationGate,
  type ProState,
} from "@/lib/pro-gate";
import {
  bookingLandingVerdict,
  consumesBookingLanding,
  firstActiveBookingId,
  nextBookingLandedFor,
  type BookingLandingState,
} from "@/lib/booking-landing";
import { BottomNav } from "@/components/BottomNav";
import { CartFab } from "@/components/cart/CartFab";
import { SplashGate } from "@/components/SplashGate";
import { IncomingRequestHost } from "@/components/provider/IncomingRequestHost";
import { AuthFlow } from "@/components/AuthFlow";
import { ConsumerLocationGate, ProviderLocationGate } from "@/components/LocationGateHost";
import { useLocationConfirmed } from "@/hooks/useLocationConfirmed";
import { useLocationAvailability } from "@/hooks/useLocationAvailability";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useEdgeSwipeBack } from "@/hooks/useEdgeSwipeBack";
import { readSelectedAddressCache } from "@/hooks/useSelectedAddress";
import { shouldGateLocation } from "@/lib/should-gate-location";
import { AppVersionGate } from "@/components/AppVersionGate";
import { FirstRunLanguagePrompt } from "@/components/language/FirstRunLanguagePrompt";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initSentry } from "@/lib/sentry";
import { useSentryNavigationBreadcrumbs } from "@/lib/useSentryNavigationBreadcrumbs";

// Web-build init. `initSentry` is idempotent + SSR-safe (window guard), so
// this coexists cleanly with the mobile build's own init in main.mobile.tsx.
initSentry();

const queryClient = new QueryClient();

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

// The full HTML shell (doctype, <head>, <body>) is provided by the static
// entry files (`index.mobile.html` for the Capacitor SPA build, and the
// marketing/dist HTML for the web build). This root route only renders the
// in-page tree, so no `shellComponent` is declared — nesting an <html>
// element inside <div id="root"> breaks React 19 hydration and results in
// a white screen. Meta/link tags belong in those HTML files.
export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function AppGate({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isNewUser, isRestoring } = useAuth();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isProfileRoute = pathname.startsWith("/profile");
  const isAdminRoute = pathname.startsWith("/admin");
  const isProviderRoute = pathname.startsWith("/provider");
  const isProviderOnboardingRoute = pathname.startsWith("/provider/onboarding");
  // The gate sends providers here for the full map + radius picker, so it must
  // not then block that route.
  const isProviderLocationRoute = pathname.startsWith("/provider/location");
  // Public routes — reachable without login.
  // - SVC-37: legal pages (privacy, terms, refund) are hit anonymously by
  //   Razorpay merchant review, Play Store data-safety review, and App Store
  //   privacy nutrition reviewers. An AuthFlow gate would kill the submission.
  // - SVC-161: /download is the pre-signup landing page — auth would defeat
  //   the point (users haven't installed the app yet).
  // All rendered as full-width layout with no BottomNav.
  const isPublicRoute =
    pathname === "/privacy" ||
    pathname === "/terms" ||
    pathname === "/refund" ||
    pathname === "/download";
  const role = user?.role ?? null;
  const isProvider = isAuthenticated && role === "provider";

  // Only fetch the provider profile (drives the onboarding gate below) once
  // we know they're actually a provider — otherwise every consumer would fire
  // an extra API call at boot.
  const { data: providerProfile, isLoading: providerProfileLoading } =
    useProviderProfile(isProvider);

  // Subscribed, not read: a plain module read would not re-render when the user
  // answers, leaving the gate on screen forever. See useLocationConfirmed.
  const locationConfirmed = useLocationConfirmed();

  // SVC-351: trusted in one direction only — `false` is proof we are offline
  // (airplane mode flips it synchronously), `true` is not proof the backend is
  // reachable (Android WebView lies). The gate below reads `false` as the
  // fast-path short-circuit for the "spinner forever" bug; the first-fetch
  // outcome (`addressesError`) is what ultimately decides on a connection
  // that `onLine` reports as up but the radio has quietly dropped.
  const isOnline = useOnlineStatus();

  // SVC-281: can we locate them at all? The gate appears only when the answer
  // is no.
  //
  // Held until authenticated. This component mounts at launch, with the user
  // still on the login screen, and the hook resolves once and keeps its answer
  // — so probing here meant probing before the session existed, then showing a
  // stale "cannot locate" gate to someone whose location worked fine.
  const availability = useLocationAvailability(isAuthenticated);

  // Only to answer "do they have *any* saved address" — a consumer with none
  // cannot book, so the gate is how they get one. Gated on being a
  // non-provider so providers and logged-out visitors don't fire the call.
  const {
    data: addresses,
    isPending: addressesPending,
    isError: addressesError,
  } = useAddresses(isAuthenticated && !isProvider);

  // SVC-322: is this consumer also a Pro? Team Pros sign up as consumers, so
  // the role cannot say; `/pros/me` can. Asked only of consumer-role users —
  // a provider's Pro row is their Pro-for-self, and they stay in the provider
  // app. See `pro-gate.ts` for what the answer decides.
  const isConsumer = isAuthenticated && role !== "provider" && role !== "admin";
  const {
    data: proMe,
    isPending: proMePending,
    isError: proMeError,
  } = useProMe(user?.id, isConsumer);
  const proState: ProState = !isConsumer
    ? "not-pro"
    : proMeError
      ? "error"
      : proMePending
        ? "loading"
        : isTeamPro(proMe)
          ? "pro"
          : "not-pro";
  // SVC-322: someone who opened /invite came to join a team. Remembered for the
  // session, because a new invitee is sent to name setup first and saving it
  // would otherwise drop them on "/" — see `src/lib/invite.ts`. Dropped again
  // on the ordinary sign-in screen, so backing out of /invite undoes it.
  const isInviteRoute = pathname === "/invite";
  const signedOut = !isAuthenticated && !isRestoring;
  useEffect(() => {
    syncPendingInvite({ onInviteRoute: isInviteRoute, signedOut, provider: isProvider });
  }, [isInviteRoute, signedOut, isProvider]);

  // SVC-322: an online team Pro sends their location every 30 s, on whatever
  // screen they are on — so it runs here, not on Pro Home.
  useProHeartbeatHost(isTeamPro(proMe) && proMe.is_online);

  // Which sign-in has had its launch landing in the Pro app. A ref keyed by
  // user rather than a boolean, so signing out and in as someone else lands
  // them afresh; cleared on sign-out, so the same person signing back in does
  // too (see `nextLandedFor`).
  const landedFor = useRef<string | null>(null);
  const landed = !!user?.id && landedFor.current === user.id;

  // SVC-385: a parallel ref for the "do we have a job in flight?" pre-empt.
  // Separate from `landedFor` because the two decisions are independent — a
  // Pro landing in /pro does not consume the consumer-side booking landing,
  // and a consumer who navigates to tracking in-session does not consume the
  // Pro-gate landing. One shared ref would conflate the two.
  const bookingLandedForRef = useRef<string | null>(null);
  const bookingLanded = !!user?.id && bookingLandedForRef.current === user.id;

  // The query the booking-landing redirect reads. Enabled only for consumers
  // past profile setup, so providers and new invitees don't fire the call. On
  // other screens it is still enabled — the home nav tab + tracking screens
  // read the same cache, so this is pre-fetching, not an extra round trip.
  const {
    data: userBookings,
    isPending: bookingsPending,
    isError: bookingsError,
  } = useBookings(undefined, isConsumer && !isRestoring);
  const bookingLandingState: BookingLandingState = !isConsumer
    ? "none"
    : bookingsError
      ? "error"
      : bookingsPending
        ? "loading"
        : firstActiveBookingId(userBookings) !== null
          ? "has-active"
          : "none";
  // Whether this render actually reaches the Pro gate in the consumer branch
  // below. The effect runs after *every* render, including the ones that
  // return early — and on device it used up the landing during the blank
  // restoring splash (the token refresh let /pros/me answer before /users/me
  // did), so the Pro was let through to the consumer home. The name-setup
  // redirect to /profile, which also fires on "/", did the same to every new
  // invitee. Mirrors the branch conditions; keep the two in step.
  const needsProfileSetup = !!user && (isNewUser || !user.name) && !isProfileRoute;
  const reachesProGate =
    isConsumer && !isRestoring && !isPublicRoute && !isAdminRoute && !needsProfileSetup;
  useEffect(() => {
    landedFor.current = nextLandedFor(landedFor.current, {
      userId: user?.id,
      signedOut,
      consumes: consumesLanding(pathname, proState, reachesProGate),
    });
  }, [user?.id, signedOut, pathname, proState, reachesProGate]);

  // SVC-385: reaches the booking landing when the consumer is actually being
  // shown their own home ("/"), or has already been redirected to tracking.
  // A new invitee on /profile for name setup does not consume: they are not
  // on home yet, and the first time they land on "/" after setup is their
  // real cold-launch landing.
  const reachesBookingLanding =
    isConsumer && !isRestoring && !isPublicRoute && !isAdminRoute && !needsProfileSetup;
  useEffect(() => {
    bookingLandedForRef.current = nextBookingLandedFor(bookingLandedForRef.current, {
      userId: user?.id,
      signedOut,
      consumes: consumesBookingLanding(pathname, bookingLandingState, reachesBookingLanding),
    });
  }, [user?.id, signedOut, pathname, bookingLandingState, reachesBookingLanding]);

  // Wire push taps → in-app navigation: native taps through the Capacitor
  // plugin (lazy-loaded so the web bundle stays clean), web-push taps through
  // the URL the service worker opens. Must live inside <AuthProvider/> for
  // `useAuth()` access — that's why it sits here, not in main.mobile.tsx.
  useNotificationRouter();

  // SVC-67: obtain a device token and store it against the user, so the
  // backend has somewhere to send. Sits beside the router hook for the same
  // reason — it needs auth state, and this component is shared by the web and
  // Capacitor builds, so both platforms register from one place.
  useFcmRegistration(isAuthenticated);

  // Record every SPA navigation as a Sentry breadcrumb so a subsequent
  // error report carries the last N routes the user visited. Cheap; no-op
  // when Sentry isn't initialized (dev + no-DSN). Placed here rather than
  // in RootComponent so it inherits the same router context every other
  // AppGate hook does.
  useSentryNavigationBreadcrumbs();

  // SVC-391: iOS left-edge-swipe → back, routed through the same interceptor
  // chain the hardware back button uses so open sheets and the AuthFlow OTP
  // step claim the gesture the same way. No-op on Android (system owns the
  // gesture) and on the web (browser does). Pathname-aware so routes where
  // back is wrong — mid-payment on /cart, the public /invite landing — stay
  // out of it.
  const edgeSwipeRouter = useRouter();
  useEdgeSwipeBack(edgeSwipeRouter, pathname);

  // Every branch below carries `safe-top` (SVC-285), and a new one must too.
  //
  // It keeps content out of the iOS status bar and paints the strip brand
  // blue — see the utility in styles.css for why only the iOS PWA needs it and
  // why it is inert everywhere else. Applied here rather than on each screen's
  // header because routes then *inherit* it: the nine sticky headers and the
  // four provider screens that have no header at all are all covered, and a
  // screen added later cannot forget a class it never opts into. Forgetting it
  // is exactly how this bug happened.
  //
  // All five branches need it, including the ones that look like they would
  // not: `isRestoring` renders a bare splash and the unauthenticated branch
  // renders the login flow, both full-height, both against the status bar.

  // Public routes render regardless of auth state.
  if (isPublicRoute) {
    return <div className="safe-top min-h-screen bg-background">{children}</div>;
  }

  // Cold-start session restore — hold the shell blank while AuthProvider
  // probes stored tokens against /users/me. Without this guard, a signed-in
  // user briefly sees the AuthFlow ("Continue with phone") flash before
  // landing on home, which reads as "did I get logged out?". Blank splash
  // is honest — we don't yet know what to render.
  if (isRestoring) {
    return <div className="safe-top mx-auto max-w-md min-h-screen bg-background" />;
  }

  // SVC-345: admin login is its own entry point, handled before the consumer
  // AuthFlow fallback below. An unauthenticated user hitting any other
  // /admin/* route is redirected here (not into AuthFlow — that lands them on
  // the consumer home after signin and never on the admin surface). An
  // already-signed-in admin arriving here hops onto /admin/kyc, so a stale
  // bookmark doesn't force a redundant login screen. /admin/login is the only
  // /admin/* route reachable without auth, kept apart from `isPublicRoute`
  // because it is still role-aware. A signed-in admin anywhere outside /admin
  // also goes to /admin/kyc, as a provider is kept under /provider. The rules
  // live in `adminGate` (SVC-329).
  const adminVerdict = adminGate(pathname, isAuthenticated, role);
  if (adminVerdict === "to-kyc") {
    return <Navigate to="/admin/kyc" replace />;
  }
  if (adminVerdict === "login-page") {
    return <div className="safe-top min-h-screen bg-background">{children}</div>;
  }
  if (adminVerdict === "to-login") {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isAuthenticated) {
    return (
      <div className="safe-top mx-auto max-w-md min-h-screen bg-background relative">
        <AuthFlow onComplete={() => {}} invite={isInviteRoute} />
      </div>
    );
  }

  // Role-based landing (SVC-41). Providers live under /provider/*; consumers
  // live everywhere else. Bouncing here means push-tap navigation from
  // notifications lands cleanly on the right surface even if the URL was
  // constructed for the wrong role.
  //
  // Every redirect in this guard is `replace` (SVC-273). These are corrections,
  // not navigation: the route the user was on was wrong for who they are, so it
  // must not become somewhere "back" can return to.
  //
  // Found on device: without it, a provider launching the app went "/" →
  // /provider and that left a history entry. `history.length` was 2 on a clean
  // launch, so back popped the entry, this guard immediately redirected again,
  // and the provider stayed put — the double-tap-to-exit branch in
  // main.mobile.tsx was unreachable, and the app could not be exited with the
  // back button at all.
  if (role === "provider") {
    // SVC-354: an owner who opens an invite link is shown /invite, which says
    // why it can't add them, instead of a silent bounce. Nothing to onboard or
    // locate for on that screen either. See `providerSeesInviteRoute`.
    const ownerOnInvite = providerSeesInviteRoute(pathname);
    if (!isProviderRoute && !ownerOnInvite) {
      return <Navigate to="/provider" replace />;
    }
    // Force incomplete providers through onboarding — name missing OR KYC
    // still pending (not submitted). Once KYC is submitted/verified/rejected
    // they're free to roam the provider surface.
    if (!isProviderOnboardingRoute && !ownerOnInvite) {
      // Deliberately not waiting on the location probe (SVC-281). That call
      // can take seconds — it is what raises the native permission dialog —
      // and holding the app blank for it was a worse bug than the flicker it
      // was meant to prevent. The gate simply appears late if it appears.
      if (providerProfileLoading) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        );
      }
      const incomplete = !user?.name || providerProfile?.kyc_status === "pending";
      if (incomplete) {
        return <Navigate to="/provider/onboarding" replace />;
      }

      // SVC-281: ask about the service area only when something is actually
      // wrong — either we cannot locate them, or they have no area at all.
      //
      // This used to fire on every launch. A service area is a standing
      // setting, not a daily decision: a provider whose area is Karve Nagar
      // has that area whether the app is open or not, and re-asking daily was
      // pure friction that also invited a mis-tap moving their geo-fence.
      //
      // The null-area clause is not optional and is *not* about location being
      // unavailable. No `service_area_center` means the PostGIS dispatch query
      // excludes them from every broadcast — online, verified, subscribed, and
      // receiving nothing, permanently, with nothing on screen explaining why.
      // That is a worse failure than a stale centre, so it gates regardless of
      // whether we can locate them.
      //
      // After onboarding on purpose — the wizard already asks (SVC-257), and
      // asking again inside it would be the same question twice.
      const providerVerdict = shouldGateLocation({
        confirmed: locationConfirmed,
        blocked: availability.isResolving ? null : availability.blocked,
        hasOwnLocation: providerProfile?.service_area_lat != null,
      });
      if (!isProviderLocationRoute && providerVerdict === "gate") {
        return (
          <ProviderLocationGate
            lat={providerProfile?.service_area_lat ?? null}
            lng={providerProfile?.service_area_lng ?? null}
            radiusKm={providerProfile?.service_area_radius_km ?? 10}
            reason={availability.reason}
          />
        );
      }
    }
  } else if (role !== "admin") {
    // Consumer path — bounce them out of /provider/*, force through profile
    // setup for fresh registrations (name missing).
    if (isProviderRoute) {
      return <Navigate to="/" replace />;
    }
    if (needsProfileSetup) {
      return <Navigate to="/profile" replace />;
    }

    // SVC-322: the Pro app lives inside the consumer role. Before the location
    // gate, deliberately: a Pro is not booking anything, and an invitee with
    // no saved address would otherwise be stopped by a "where are you?" screen
    // before ever reaching their jobs.
    const proVerdict = proGateVerdict({ pathname, proState, landed });
    if (proVerdict === "wait") {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (proVerdict === "to-pro") {
      return <Navigate to="/pro" replace />;
    }
    if (proVerdict === "to-home") {
      return <Navigate to="/" replace />;
    }

    // SVC-385: a consumer whose last session left a booking in flight comes
    // back to look at the tracker, not to shop. Pre-empt home ONCE per
    // sign-in — later nav back to "/" during the same session respects user
    // intent. The waiting spinner here is the same one proGate uses while
    // deciding Pro-or-consumer; one shared "we're deciding where to send you"
    // moment is honest, and racing the two decisions in parallel keeps the
    // combined wait equal to whichever is slower rather than their sum.
    const bookingVerdict = bookingLandingVerdict({
      pathname,
      state: bookingLandingState,
      landed: bookingLanded,
    });
    if (bookingVerdict === "wait") {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (bookingVerdict === "to-tracking") {
      const activeId = firstActiveBookingId(userBookings);
      // The verdict and the id-lookup read the same list, so an id must
      // exist here — the type refinement is for TypeScript's benefit.
      if (activeId) {
        return <Navigate to="/tracking/$bookingId" params={{ bookingId: activeId }} replace />;
      }
    }

    // SVC-281: ask for a location only when we cannot determine one.
    //
    // This used to fire on every launch, on the theory that the costly failure
    // is a *stale* address rather than a missing one. The stale case is real —
    // someone in Pune yesterday and Mumbai today books against their Pune
    // address and the booking dies as "no partners found nearby" — but asking
    // everyone daily does not reliably catch it: a daily prompt becomes a
    // reflex, and a reflex cannot notice the city is wrong.
    //
    // So the gate now appears only when we genuinely do not know where they
    // are, or they have no address to book against at all.
    if (isAuthenticated && !skipsLocationGate(pathname, proState)) {
      // SVC-351: offline collapses the "still fetching" state into a decided
      // one. React Query defaults to `networkMode: 'online'` and pauses
      // queries while `navigator.onLine === false` — isPending stays true,
      // isError stays false, and without this branch the spinner below rode
      // that pause forever, which is the whole bug. The cached address lets
      // us bypass the gate entirely for a returning user; a fresh install
      // with nothing cached falls through to the gate's offline branch.
      const cached = !isOnline && addressesPending ? readSelectedAddressCache() : null;
      const verdict = shouldGateLocation({
        confirmed: locationConfirmed,
        blocked: availability.isResolving ? null : availability.blocked,
        // A failed fetch resolves to `false`, not `null`. Leaving it null on
        // error would spin forever once React Query gives up retrying — the
        // same lockout shape as #121, and offline is exactly when it happens.
        // Gating instead is safe: the gate's manual entry needs no network.
        //
        // Offline + pending collapses the same way: if we have a cached
        // selected address, treat it as evidence of a location on file and
        // let the user into the app with a stale LocationBar; otherwise fall
        // through to the gate, which renders its own offline branch rather
        // than a spinner.
        hasOwnLocation: addressesError
          ? false
          : addressesPending
            ? !isOnline
              ? cached
                ? true
                : false
              : null
            : !!addresses?.length,
      });
      // Only the address query is worth a spinner — it is one fast request.
      // The location probe is not: it can take seconds, since it is the call
      // that raises the native permission dialog, and blocking on it would
      // mean a blank launch for everyone. So the app renders while it runs and
      // the gate appears only once the answer comes back negative.
      //
      // Online-only: when `navigator.onLine` is false the query is paused and
      // never resolves, so a spinner here is exactly the infinite one SVC-351
      // kills. The branch above has already decided what to show in that case.
      if (verdict === "wait" && addressesPending && !addressesError && isOnline) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        );
      }
      if (verdict === "gate") {
        return (
          <ConsumerLocationGate
            reason={availability.reason}
            // Only when the browser is certain we are offline. A failed fetch
            // on an "online" device could be a 5xx the gate's normal path
            // handles perfectly well — treating that as offline would send a
            // user with a working connection to a Retry screen rather than
            // the manual-entry escape they already have below.
            offline={!isOnline}
            // Hard reload: the location probe caches its answer in a hook that
            // only re-runs on `enabled` flipping, and React Query's paused
            // state is tied to a listener we do not want to replicate here.
            // A reload also walks the user back through the auth cold-start,
            // which is the honest version of "try again".
            onRetry={() => {
              if (typeof window !== "undefined") window.location.reload();
            }}
          />
        );
      }
    }
  }

  // Admin pages render full-width; consumer/provider keep the max-w-md mobile shell.
  // The admin menu sits here, once, so every admin page has it (SVC-329); the
  // login page returns earlier and never shows it.
  if (isAdminRoute) {
    return (
      <div className="safe-top min-h-screen bg-background">
        <AdminMenu />
        {children}
      </div>
    );
  }
  return (
    <div className="safe-top mx-auto max-w-md min-h-screen bg-background pb-20">
      {children}
      {/* SVC-375: asks a provider or Pro for a language once after sign-in,
          only while i18n_enabled. Here, in the app shell, so it never stacks
          on a gate; the rules are in `shouldAskLanguage`. */}
      <FirstRunLanguagePrompt />
    </div>
  );
}

function RootComponent() {
  // ErrorBoundary sits above the providers so an unhandled render error in
  // any of them (AuthProvider init, QueryClient boot, AppVersionGate mount)
  // still gets caught + reported to Sentry — without this the web/SSR build
  // shows a white screen with nothing in Sentry. The mobile entry
  // (main.mobile.tsx) already has the same wrap; this brings the web path
  // to parity. Route-level errors (inside <Outlet/>) are still handled by
  // TanStack Router's `defaultErrorComponent` (RouterErrorFallback).
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* SVC-375: the language each screen renders in, decided from the
              route during render — Hindi/Marathi only on /provider and /pro
              while i18n_enabled. Inside AuthProvider for the user, and above
              everything that renders copy, the overlays included. */}
          <LanguageProvider>
            {/* CartProvider sits inside AuthProvider so the cart can be
              scoped by user id (see cart-context.tsx). Outside the
              version gate so a version-blocked user's cart still
              survives an update. */}
            <CartProvider>
              {/* SVC-290 sheets: CategorySheetProvider hosts the L1 →
                sub-categories picker; ServiceSheetProvider hosts the
                service detail sheet. Both sit above SplashGate so a
                deep-link redirect (either /category/{L1-slug} or
                /service/{id}) can safely open its sheet before the
                splash lifts. */}
              <CategorySheetProvider>
                <ServiceSheetProvider>
                  {/* SplashGate is the outermost UI concern: the animated
                    launch splash covers everything for ~2s on cold app
                    open, then swaps in the app shell. Sits inside all the
                    providers so the app underneath is fully hydrated by
                    the time the splash lifts. */}
                  <SplashGate>
                    {/* SVC-162: version-check nudge/block overlays. Wraps
                      the whole tree so the block state can cover
                      everything below it. */}
                    <AppVersionGate>
                      <AppGate>
                        <Outlet />
                        <BottomNav />
                        <CartFab />
                      </AppGate>
                      {/* SVC-69: full-screen overlay for an incoming
                        broadcast job. Sits outside AppGate so it can
                        cover the whole shell; renders nothing unless a
                        booking id is supplied. */}
                      <IncomingRequestHost />
                      <Toaster />
                    </AppVersionGate>
                  </SplashGate>
                </ServiceSheetProvider>
              </CategorySheetProvider>
            </CartProvider>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
